import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth';
import { useEffect, useRef, useState } from 'react';
import { getAuthDeferred } from '@/utils/firebase';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';
import { GreetLogo } from '@/components/brand/GreetLogo';
import Loader from '@/components/Loader';
import { sendVerificationEmail } from '@/api';
import { prefetchDashboard } from '@/lib/prefetch-dashboard';

export const Route = createFileRoute('/verify-email')({
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { isAuthenticated, isInitialLoading, logout, getUser, setUser } =
    useAuth();
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const auth = getAuthDeferred();

  // Poll Firebase for email verification status
  useEffect(() => {
    if (!auth.currentUser) return;

    const checkVerified = async () => {
      try {
        await auth.currentUser?.reload();
        if (auth.currentUser?.emailVerified) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          setVerifying(true);
          // Refresh the token so backend sees updated email_verified
          const token = await auth.currentUser.getIdToken(true);
          const userDetails = await getUser(token);
          if (userDetails) {
            setUser(userDetails);
          }
          await prefetchDashboard();
          navigate({ to: '/dashboard' });
        }
      } catch {
        // Ignore reload errors
      }
    };

    // Check immediately in case user just verified
    checkVerified();

    intervalRef.current = setInterval(checkVerified, 3000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [auth.currentUser]);

  // If not authenticated, redirect to login
  useEffect(() => {
    if (!isInitialLoading && !isAuthenticated && !auth.currentUser) {
      navigate({ to: '/' });
    }
  }, [isAuthenticated, isInitialLoading, auth.currentUser]);

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    try {
      const result = await sendVerificationEmail();
      if (!result.error) {
        toast.success('Verification email sent');
      } else if (result.response?.status === 429) {
        toast.error('Just sent one, give it a minute');
      } else {
        toast.error('Failed to send email. Try again later.');
      }
    } catch {
      toast.error('Failed to send email. Try again later.');
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = async () => {
    await logout();
    navigate({ to: '/' });
  };

  const email = auth.currentUser?.email ?? '';

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <div className="text-center">
          <Loader />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
            Redirecting...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950 p-6">
      <div className="w-full max-w-sm text-center">
        <GreetLogo className="h-8 w-auto mb-8 mx-auto" />
        <div className="flex justify-center mb-6">
          <InboxIcon3D className="h-20 w-20" />
        </div>
        <h1 className="text-xl font-semibold mb-2">Check your email</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          We sent a verification link to{' '}
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {email}
          </span>
        </p>
        <div className="space-y-3">
          <Button
            onClick={handleResend}
            disabled={resending}
            variant="outline"
            className="w-full"
          >
            {resending ? 'Sending...' : 'Resend email'}
          </Button>
          <button
            onClick={handleBackToLogin}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
}

function InboxIcon3D({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <defs>
        <linearGradient id="inbox-inside" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#27272A" />
          <stop offset="1" stopColor="#52525B" />
        </linearGradient>
        <linearGradient id="inbox-front" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#52525B" />
          <stop offset="1" stopColor="#09090B" />
        </linearGradient>
        <linearGradient id="inbox-letter" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#E4E4E7" />
        </linearGradient>
        <linearGradient id="inbox-flap" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#D4F98A" />
          <stop offset="1" stopColor="#86C424" />
        </linearGradient>
        <radialGradient id="inbox-shadow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#000" stopOpacity="0.22" />
          <stop offset="1" stopColor="#000" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="32" cy="57" rx="24" ry="4" fill="url(#inbox-shadow)" />
      {/* tray inside */}
      <path d="M13 28 H51 L56 40 H8 Z" fill="url(#inbox-inside)" />
      {/* envelope */}
      <rect x="17" y="10" width="30" height="36" rx="3" fill="url(#inbox-letter)" />
      <path d="M17 13 A3 3 0 0 1 20 10 H44 A3 3 0 0 1 47 13 L32 24 Z" fill="url(#inbox-flap)" />
      <path d="M21 12.5 H30" stroke="#fff" strokeOpacity="0.6" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M23 30 H41 M23 35 H35" stroke="#D4D4D8" strokeWidth="2" strokeLinecap="round" />
      {/* tray front */}
      <path
        d="M8 40 H21 C22.5 44 26 46.5 32 46.5 C38 46.5 41.5 44 43 40 H56 V50 A4 4 0 0 1 52 54 H12 A4 4 0 0 1 8 50 Z"
        fill="url(#inbox-front)"
      />
      <path
        d="M11 42.5 H19.5 C21.5 46.5 25.5 49 32 49"
        stroke="#fff"
        strokeOpacity="0.25"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
      {/* notification dot */}
      <circle cx="47" cy="11" r="5" fill="#AAE73D" stroke="#fff" strokeWidth="2" />
    </svg>
  );
}
